import React, { useContext, useState } from 'react';
import { Modal } from 'components/containers/Modal';
import { ButtonColorsEnum } from 'constants/button-colors';
import { Button } from '../../../../components/actions/Button';
import { IconsEnum } from '../../../../constants/icons';
import { Void } from '../../../../models/basic-objects';
import { showToast } from '../../../../helpers/showToast';
import { PhotoExtensions, VideoExtensions, MaxLimits } from '../../../../constants/media';
import { enumValues } from '../../../../helpers/enumEnumerator';
import { DropZone } from '../../../../components/actions/DropZone';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import classes from './AddMediaSelectorModal.module.scss';
import classNames from 'classnames';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { BreakpointsEnum } from '../../../../constants/breakpoints';
import { getVideoCover } from '../../../../helpers/getVideoCover';
import { createImageThumbnail } from '../../../../helpers/createImageThumbnail';
import { Loader } from '../../../../components/containers/Loader';
import {
  getDetailText,
  getUniqueFileRejectionErrors,
  UniqueFileRejectionErrorType
} from '../../../../helpers/fileUploadHelpers';
import { fileValidator } from '../../../../components/actions/DropZone/DropZone.view';
import { PostEditorContext } from '../../../PostEditor/states/PostEditorProvider';
import { MediaProportions } from '../../../../models/graphql';
import { SourceType } from '../../../../hooks/useMediaUpload';
import { getUploadIdFromFileOrUrl } from '../../../../helpers/media';

export type FileWithPreview = File & {
  preview: string; //blob image path
  id: string; // we generate an unique id
  gif: boolean; // detect if file is gif
  proportions: MediaProportions;
  width: number;
  height: number;
  length: number | null;
  sourceType: SourceType.file;
  startTranscode?: boolean;
  fileSize: number;
};

export type FilePreviewError = {
  name: string;
  type: string;
  error: string;
};

export type AddMediaSelectorModalContextProps =
  | {
      context?: 'post_editor';
      addFromLibraryClickHandler: VoidFunction;
      children?: React.ReactNode;
    }
  | {
      context?: never;
      addFromLibraryClickHandler?: never;
      children?: never;
    };

export type AddMediaSelectorModalViewProps = {
  onClose: Void;
  mediaByUrlClickHandler: VoidFunction;
  findImagesClickHandler: VoidFunction;
  fileUploadHandler: (files: FileWithPreview[]) => void;
  thumbnailSize?: 320 | 180;
  designInCanvaClickHandler: VoidFunction;
} & AddMediaSelectorModalContextProps;

const selectorIconButton = (title: string, icon: IconsEnum, action?: Void): JSX.Element => {
  const labelClasses = classNames('margin-top-small-8', classes.SelectorIconButtonLabel);

  return (
    <div onClick={action} className={classes.IconButton}>
      <div className={classes.Button}>
        <Button
          type={'a'}
          variant={'solid'}
          size={'large'}
          color={ButtonColorsEnum.primary}
          rounded={true}
          icon={{ side: 'center', value: icon }}
        />
      </div>
      <div className={labelClasses}>{title}</div>
    </div>
  );
};

export const AddMediaSelectorModalView = (props: AddMediaSelectorModalViewProps): JSX.Element => {
  const { initialized: isInPostEditor } = useContext(PostEditorContext);

  const [loading, setLoading] = useState<boolean>(false);
  const acceptedTypes: Accept = {};

  enumValues(PhotoExtensions).forEach(v => {
    acceptedTypes[`image/${v}`] = [];
  });

  enumValues(VideoExtensions).forEach(v => {
    acceptedTypes[`video/${v}`] = [];
  });

  const breakpoint = useBreakpoints();

  const showToastMessage = (uniqueError: UniqueFileRejectionErrorType) => {
    const detailText = getDetailText(uniqueError.code, uniqueError.files.length);

    showToast({
      severity: 'error',
      summary: uniqueError.summary,
      detail: detailText
    });
  };

  const onDropRejectedHandler = (fileRejections: FileRejection[]) => {
    const uniqueFileRejectionErrors = getUniqueFileRejectionErrors(fileRejections);
    uniqueFileRejectionErrors.forEach(showToastMessage);
  };

  const asyncFileMap = (files: File[]): Promise<FileWithPreview[]> => {
    let errors: FilePreviewError[] = [];
    const promises = files.map(async file => {
      let preview: string;
      let startTranscode = false;
      let proportions = MediaProportions.SQUARE;
      let width = 1;
      let height = 1;
      const sourceType: SourceType.file = SourceType.file;
      let length = null;
      const id = getUploadIdFromFileOrUrl(file);
      // let preview = URL.createObjectURL(file);
      if (file.type.includes('video')) {
        try {
          const videoCover = await getVideoCover(file, undefined, props.thumbnailSize);
          // Only set to true if we are NOT in the post editor
          startTranscode = !isInPostEditor;
          preview = videoCover.preview;
          proportions = videoCover.proportion;
          width = videoCover.width;
          height = videoCover.height;
          length = videoCover.length;
          // Only set to true if we are NOT in the post editor
          startTranscode = !isInPostEditor;
        } catch (e: unknown) {
          errors = [...errors, { name: file.name, type: file.type, error: e as string }];
          return Object.assign(file, {
            preview: '',
            id,
            gif: false,
            proportions: MediaProportions.SQUARE,
            width,
            height,
            length,
            sourceType,
            fileSize: file.size
          });
        }
      } else {
        try {
          const thumbnail = await createImageThumbnail(file, props.thumbnailSize);
          preview = thumbnail.preview;
          proportions = thumbnail.proportion;
          width = thumbnail.width;
          height = thumbnail.height;
          length = thumbnail.length;
        } catch (e: unknown) {
          errors = [...errors, { name: file.name, type: file.type, error: e as string }];
          return Object.assign(file, {
            preview: '',
            id,
            gif: file.type.includes('gif'), // @TODO: this is not the correct way to determine if a gif (animated)
            proportions: MediaProportions.SQUARE,
            width,
            height,
            length,
            sourceType,
            fileSize: file.size
          });
        }
      }
      return Object.assign(file, {
        preview,
        proportions,
        id,
        gif: file.type.includes('gif'),
        width,
        height,
        length,
        startTranscode,
        sourceType,
        fileSize: file.size
      });
    });
    if (errors.length) {
      showToast({
        severity: 'error',
        summary: 'Video Cover Preview Failed',
        detail: (
          <>
            We could not create video cover for the following videos:
            <br />
            <strong>{errors.map(error => error.name).join(' ')}</strong>
          </>
        )
      });
    }
    return Promise.all(promises);
  };

  const onAcceptedHandler = (files: File[]) => {
    setLoading(true);
    asyncFileMap(files)
      .then(result => {
        props.fileUploadHandler(result);
      })
      .catch(e => {
        // error
        console.error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: MaxLimits.UPLOAD,
    noClick: false,
    noKeyboard: true,
    accept: acceptedTypes,
    onDropRejected: onDropRejectedHandler,
    onDropAccepted: (files: File[]) => onAcceptedHandler(files),
    validator: fileValidator
  });

  const wrapperClasses = classNames('grid-x', classes.SelectorWrapper, {
    'align-justify': breakpoint === BreakpointsEnum.small
  });

  return (
    <Modal
      onClose={props.onClose}
      closeOnClickOutside={false}
      showCloseIcon={true}
      title={'Add Media'}
      size={'large'}
      compensateForScroll={true}>
      <>
        {loading ? (
          <div className={classes.LoadingWrapper}>
            <div className={classes.LoadingContainer}>
              <Loader variation={'dark'} size={'small'} type={'inline'} />
            </div>
          </div>
        ) : (
          <DropZone
            acceptedTypes={acceptedTypes}
            onDropRejectedHandler={onDropRejectedHandler}
            onDropAcceptedHandler={onAcceptedHandler}>
            <div className={wrapperClasses}>
              <div className={classes.SelectorButtonContainer}>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  {selectorIconButton('Upload Media', IconsEnum.upload)}
                </div>
                {props.context && (
                  <>{selectorIconButton('Media Library', IconsEnum.photo, props.addFromLibraryClickHandler)}</>
                )}
                {selectorIconButton('Find Free Images', IconsEnum.search, props.findImagesClickHandler)}
                {selectorIconButton('Paste from URL', IconsEnum.link, props.mediaByUrlClickHandler)}
                {selectorIconButton('Design in Canva', IconsEnum.canva, props.designInCanvaClickHandler)}
              </div>
            </div>
          </DropZone>
        )}
        {props.context ? props.children : undefined}
      </>
    </Modal>
  );
};
